import React, { useMemo } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  TextInput,
  useTheme,
  SegmentedButtons,
  Chip,
  Card,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';

// Currency configurations with symbols and conversion rates (approximate)
const currencyConfig = {
  USD: {
    symbol: '$',
    label: 'USD',
    rates: {
      budget: 50,
      midRange: [50, 200],
      premium: [200, 500],
      luxury: 500,
    },
  },
  EUR: {
    symbol: '€',
    label: 'EUR',
    rates: {
      budget: 45,
      midRange: [45, 180],
      premium: [180, 450],
      luxury: 450,
    },
  },
  GBP: {
    symbol: '£',
    label: 'GBP',
    rates: {
      budget: 40,
      midRange: [40, 160],
      premium: [160, 400],
      luxury: 400,
    },
  },
  JPY: {
    symbol: '¥',
    label: 'JPY',
    rates: {
      budget: 7000,
      midRange: [7000, 28000],
      premium: [28000, 70000],
      luxury: 70000,
    },
  },
  CAD: {
    symbol: 'C$',
    label: 'CAD',
    rates: {
      budget: 65,
      midRange: [65, 260],
      premium: [260, 650],
      luxury: 650,
    },
  },
  AUD: {
    symbol: 'A$',
    label: 'AUD',
    rates: {
      budget: 75,
      midRange: [75, 300],
      premium: [300, 750],
      luxury: 750,
    },
  },
};

const currencies = Object.keys(currencyConfig).map(key => ({
  value: key,
  label: currencyConfig[key as keyof typeof currencyConfig].label,
  symbol: currencyConfig[key as keyof typeof currencyConfig].symbol,
}));

export function EquipmentPricing() {
  const theme = useTheme();
  const colors = theme.colors;
  const isDark = theme.dark;
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const priceMin = watch('price_min');
  const priceMax = watch('price_max');
  const currency = watch('currency') || 'USD';

  // Get currency symbol
  const currencySymbol =
    currencyConfig[currency as keyof typeof currencyConfig]?.symbol || '$';
  const rates = currencyConfig[currency as keyof typeof currencyConfig]?.rates;

  // Generate dynamic price ranges based on selected currency
  const priceRanges = useMemo(() => {
    if (!rates) return [];

    return [
      {
        value: 'budget',
        label: 'Budget',
        description: `< ${currencySymbol}${rates.budget}`,
      },
      {
        value: 'mid_range',
        label: 'Mid-Range',
        description: `${currencySymbol}${rates.midRange[0]}-${currencySymbol}${rates.midRange[1]}`,
      },
      {
        value: 'premium',
        label: 'Premium',
        description: `${currencySymbol}${rates.premium[0]}-${currencySymbol}${rates.premium[1]}`,
      },
      {
        value: 'luxury',
        label: 'Luxury',
        description: `${currencySymbol}${rates.luxury}+`,
      },
    ];
  }, [currency, currencySymbol, rates]);

  return (
    <View style={styles.container}>
      {/* Currency */}
      <View style={styles.fieldContainer}>
        <Text
          variant='titleMedium'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Currency
        </Text>
        <Controller
          control={control}
          name='currency'
          render={({ field: { onChange, value } }) => (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.chipRow}
              contentContainerStyle={styles.chipRowContent}
            >
              {currencies.map(curr => (
                <View key={curr.value} style={styles.chipWrapper}>
                  <Chip
                    selected={value === curr.value}
                    onPress={() => onChange(curr.value)}
                    style={styles.chip}
                    compact={false}
                    textStyle={styles.chipLabel}
                  >
                    {curr.label} ({curr.symbol})
                  </Chip>
                </View>
              ))}
            </ScrollView>
          )}
        />
      </View>

      {/* Price Range Category */}
      <View style={styles.fieldContainer}>
        <Text
          variant='titleMedium'
          style={[styles.label, { color: colors.onSurface, fontSize: 18 }]}
        >
          Price Range Category *
        </Text>
        <Text
          variant='bodyMedium'
          style={[
            styles.helperText,
            { color: colors.onSurfaceVariant, fontSize: 14, marginTop: 4 },
          ]}
        >
          Select the general price category for this equipment
        </Text>
        <Controller
          control={control}
          name='price_range'
          render={({ field: { onChange, value } }) => (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.chipRow}
              contentContainerStyle={styles.chipRowContent}
            >
              {priceRanges.map(range => (
                <View key={range.value} style={styles.chipWrapper}>
                  <Chip
                    selected={value === range.value}
                    onPress={() => onChange(range.value)}
                    style={styles.chip}
                    compact={false}
                    textStyle={styles.chipLabel}
                  >
                    {range.label} {range.description}
                  </Chip>
                </View>
              ))}
            </ScrollView>
          )}
        />
      </View>

      {/* Price Range */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Price Range
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          Set the minimum and maximum price (optional)
        </Text>

        <View style={styles.priceInputRow}>
          {/* Min Price */}
          <View style={styles.priceInputContainer}>
            <Text
              variant='labelMedium'
              style={{ color: colors.onSurfaceVariant, marginBottom: 8 }}
            >
              Minimum
            </Text>
            <Controller
              control={control}
              name='price_min'
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  mode='outlined'
                  placeholder='0.00'
                  value={value?.toString() || ''}
                  onChangeText={text => {
                    const num = parseFloat(text);
                    onChange(isNaN(num) ? undefined : num);
                  }}
                  onBlur={onBlur}
                  keyboardType='decimal-pad'
                  left={<TextInput.Affix text={currencySymbol} />}
                  style={styles.input}
                  outlineStyle={styles.inputOutline}
                />
              )}
            />
          </View>

          {/* Max Price */}
          <View style={styles.priceInputContainer}>
            <Text
              variant='labelMedium'
              style={{ color: colors.onSurfaceVariant, marginBottom: 8 }}
            >
              Maximum
            </Text>
            <Controller
              control={control}
              name='price_max'
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  mode='outlined'
                  placeholder='0.00'
                  value={value?.toString() || ''}
                  onChangeText={text => {
                    const num = parseFloat(text);
                    onChange(isNaN(num) ? undefined : num);
                  }}
                  onBlur={onBlur}
                  keyboardType='decimal-pad'
                  left={<TextInput.Affix text={currencySymbol} />}
                  style={styles.input}
                  outlineStyle={styles.inputOutline}
                />
              )}
            />
          </View>
        </View>

        {/* Price Preview */}
        {(priceMin !== undefined || priceMax !== undefined) && (
          <View
            style={[
              styles.pricePreview,
              {
                backgroundColor: isDark
                  ? 'rgba(129, 199, 132, 0.12)'
                  : 'rgba(76, 175, 80, 0.08)',
                borderColor: isDark
                  ? 'rgba(129, 199, 132, 0.3)'
                  : 'rgba(76, 175, 80, 0.2)',
              },
            ]}
          >
            <Text
              variant='labelMedium'
              style={{ color: isDark ? '#81C784' : '#2E7D32' }}
            >
              Price Display:{' '}
              {priceMin !== undefined &&
              priceMax !== undefined &&
              priceMin !== priceMax
                ? `${currencySymbol}${priceMin}-${currencySymbol}${priceMax}`
                : priceMin !== undefined
                  ? `${currencySymbol}${priceMin}`
                  : priceMax !== undefined
                    ? `Up to ${currencySymbol}${priceMax}`
                    : 'Contact for price'}
            </Text>
          </View>
        )}
      </View>

      {/* Availability Status */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Availability
        </Text>
        <Controller
          control={control}
          name='is_active'
          render={({ field: { onChange, value } }) => (
            <SegmentedButtons
              value={value ? 'available' : 'unavailable'}
              onValueChange={v => onChange(v === 'available')}
              buttons={[
                { value: 'available', label: 'Available' },
                { value: 'unavailable', label: 'Unavailable' },
              ]}
              density='small'
            />
          )}
        />
      </View>

      {/* Info Card */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Pricing
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Set a price range if the equipment has multiple variants{'\n'}•
            Use the price category to help users filter by budget{'\n'}• Leave
            prices empty if pricing varies by retailer{'\n'}• Keep pricing
            information up to date
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 24,
  },
  fieldContainer: {
    gap: 8,
  },
  label: {
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    marginTop: -4,
  },
  input: {
    fontSize: 15,
  },
  inputOutline: {
    borderRadius: 12,
  },
  chipRow: {
    marginBottom: 16,
    marginTop: 8,
  },
  chipRowContent: {
    paddingRight: 8,
    gap: 8,
  },
  chipWrapper: {
    flexShrink: 0,
  },
  chip: {
    height: 40,
    borderRadius: 20,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
  priceInputRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  priceInputContainer: {
    flex: 1,
  },
  pricePreview: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  card: {
    marginTop: 8,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
